(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-sb-naternal-offer]';

  function normalise(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function parseConfig(root) {
    const node = root.querySelector('[data-sb-offer-config]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('Solbiome offer config could not be parsed.', error);
      return null;
    }
  }

  function getCurrencyMeta(config) {
    const currency = String(config.currency || 'AUD').toUpperCase();

    const currencySymbols = {
      AUD: '$',
      NZD: '$',
      USD: '$',
      CAD: '$',
      SGD: '$',
      GBP: '£',
      EUR: '€',
      JPY: '¥'
    };

    const zeroDecimalCurrencies = ['JPY', 'KRW'];

    return {
      currency,
      symbol: currencySymbols[currency] || '',
      decimals: zeroDecimalCurrencies.includes(currency) ? 0 : 2
    };
  }

  function formatMoney(cents, config) {
    const amount = Number(cents || 0) / 100;
    const meta = getCurrencyMeta(config);

    const formattedAmount = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: meta.decimals,
      maximumFractionDigits: meta.decimals
    }).format(amount);

    if (meta.symbol) {
      return `${meta.symbol}${formattedAmount} ${meta.currency}`;
    }

    return `${formattedAmount} ${meta.currency}`;
  }

  function formatDailyMoney(cents, config) {
    const amount = Number(cents || 0) / 100;
    const meta = getCurrencyMeta(config);

    const formattedAmount = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: meta.decimals,
      maximumFractionDigits: meta.decimals
    }).format(amount);

    if (meta.symbol) {
      return `${meta.symbol}${formattedAmount}/day`;
    }

    return `${formattedAmount} ${meta.currency}/day`;
  }

  function getOption(plan, keys) {
    const options = Array.isArray(plan.options) ? plan.options : [];

    for (const option of options) {
      const name = normalise(option.name);

      if (keys.some((key) => name.includes(key))) {
        return String(option.value || '').trim();
      }
    }

    return '';
  }

  function getPlanQuantity(plan) {
    const raw = getOption(plan, ['product quantity', 'quantity']);
    const quantity = parseInt(raw, 10);

    if (quantity > 0) return quantity;

    const planName = String(plan.name || '');

    const match =
      planName.match(/(?:product quantity|quantity|qty)\s*[:\-]?\s*(\d+)/i) ||
      planName.match(/\b(\d+)\s*(?:product|products|box|boxes)\b/i);

    if (match) {
      const fallback = parseInt(match[1], 10);
      if (fallback > 0) return fallback;
    }

    return 1;
  }

  function getPlanSearchText(plan) {
    const optionText = (plan.options || [])
      .map((option) => `${option.name || ''} ${option.value || ''}`)
      .join(' ');

    return normalise(
      `${plan.name || ''} ${plan.groupName || ''} ${optionText}`
    );
  }

  function findPlan(config, desiredQuantity, frequencyKeyword) {
    const wantedFrequency = normalise(frequencyKeyword);

    const plans = (config.plans || []).map((plan) => ({
      plan,
      quantity: getPlanQuantity(plan),
      searchText: getPlanSearchText(plan)
    }));

    return (
      plans.find(
        (item) =>
          item.quantity === Number(desiredQuantity) &&
          (!wantedFrequency || item.searchText.includes(wantedFrequency))
      ) || null
    );
  }

  function makeSubscriptionOffer(
    config,
    key,
    desiredQuantity,
    frequencyKeyword,
    days
  ) {
    const match = findPlan(config, desiredQuantity, frequencyKeyword);

    if (!match) return null;

    const quantity = Number(desiredQuantity);
    const unitPrice = Number(match.plan.price || config.basePrice);
    const total = unitPrice * quantity;
    const compareTotal = Number(config.basePrice) * quantity;

    const discount =
      compareTotal > 0
        ? Math.max(0, ((compareTotal - total) / compareTotal) * 100)
        : 0;

    return {
      type: key,
      quantity,
      planId: Number(match.plan.id),
      total,
      compareTotal,
      discount,
      dailyPrice: Math.round(total / Number(days || 30))
    };
  }

  function calculateOffers(config) {
    const base = Number(config.basePrice || 0);
    const twoDiscount = Number(config.twoMonthDiscount || 5);

    return {
      one: {
        type: 'one',
        quantity: 1,
        planId: null,
        total: base,
        compareTotal: 0,
        discount: 0,
        dailyPrice: Math.round(base / 30)
      },

      two: {
        type: 'two',
        quantity: 2,
        planId: null,
        total: Math.round(base * 2 * (1 - twoDiscount / 100)),
        compareTotal: base * 2,
        discount: twoDiscount,
        dailyPrice: Math.round(
          (base * 2 * (1 - twoDiscount / 100)) / 60
        )
      },

      monthly_subscription: makeSubscriptionOffer(
        config,
        'monthly_subscription',
        config.monthlySubscription?.quantity || 1,
        config.monthlySubscription?.frequencyKeyword || '1 month',
        config.monthlySubscription?.days || 30
      ),

      three_subscription: makeSubscriptionOffer(
        config,
        'three_subscription',
        config.threeMonthSubscription?.quantity || 3,
        config.threeMonthSubscription?.frequencyKeyword || '3 month',
        config.threeMonthSubscription?.days || 90
      )
    };
  }

  function bottleMarkup(config, count) {
    if (!config.productImage) return '';

    return `
      <span class="sb-naternal-offer__bottles" data-count="${count}">
        ${Array.from(
          { length: count },
          () =>
            `<img class="sb-naternal-offer__bottle" src="${config.productImage}" alt="" loading="lazy">`
        ).join('')}
      </span>
    `;
  }

  function discountLabel(discount, suffix) {
    const rounded = Math.round(Number(discount || 0) * 10) / 10;
    const amount = Number.isInteger(rounded)
      ? rounded
      : rounded.toFixed(1);

    return `Save ${amount}%${suffix ? ` · ${suffix}` : ''}`;
  }

  function giftMarkup(config) {
    const gift = config.gift;

    if (!gift?.enabled || !gift.variantId) {
      return '';
    }

    const value = gift.price
      ? `${config.labels.giftValuePrefix || 'Valued At'} ${formatMoney(
          gift.price,
          config
        )}`
      : '';

    return `
      <span class="sb-naternal-offer__gift">
        <span class="sb-naternal-offer__gift-heading">
          ${config.labels.giftDivider || '+ Free Gift'}
        </span>

        <span class="sb-naternal-offer__gift-media">
          ${
            gift.image
              ? `<img class="sb-naternal-offer__gift-image" src="${gift.image}" alt="${gift.title || 'Free gift'}" loading="lazy">`
              : ''
          }
        </span>

        <span>
          <span class="sb-naternal-offer__gift-title">
            ${gift.title || 'Free Gift'}
          </span>

          <span class="sb-naternal-offer__gift-copy">
            Included with the 3 month subscription
          </span>
        </span>

        ${
          value
            ? `<span class="sb-naternal-offer__gift-value">${value}</span>`
            : ''
        }
      </span>
    `;
  }

  function createCard(
    config,
    offer,
    title,
    subtitle,
    options = {}
  ) {
    const button = document.createElement('button');

    button.type = 'button';
    button.className = 'sb-naternal-offer__card';
    button.dataset.offer = offer.type;
    button.setAttribute('aria-pressed', 'false');

    if (options.hero) {
      button.classList.add('is-hero');
    }

    const badge = options.badge
      ? `<span class="sb-naternal-offer__badge">${options.badge}</span>`
      : '';

    const body = options.body
      ? `<span class="sb-naternal-offer__body">${options.body}</span>`
      : '';

    const priceMarkup =
      offer.compareTotal > offer.total
        ? `
          <span class="sb-naternal-offer__compare">${formatMoney(
            offer.compareTotal,
            config
          )}</span>
          <span class="sb-naternal-offer__current">${formatMoney(
            offer.total,
            config
          )}</span>
        `
        : `
          <span class="sb-naternal-offer__current">${formatMoney(
            offer.total,
            config
          )}</span>
        `;

    button.innerHTML = `
      ${badge}

      <span class="sb-naternal-offer__media">
        ${bottleMarkup(config, offer.quantity)}
      </span>

      <span class="sb-naternal-offer__main">
        <span class="sb-naternal-offer__title">${title}</span>
        <span class="sb-naternal-offer__subtitle">${subtitle}</span>
        ${body}
      </span>

      <span class="sb-naternal-offer__price">
        ${priceMarkup}
        <span class="sb-naternal-offer__daily">${formatDailyMoney(
          offer.dailyPrice,
          config
        )}</span>
      </span>

      ${options.showGift ? giftMarkup(config) : ''}
    `;

    return button;
  }

  function findNativeProductForms(config) {
    const forms = [];

    document
      .querySelectorAll(
        `product-form-component[data-product-id="${config.productId}"] form[action*="/cart/add"]`
      )
      .forEach((form) => {
        if (!forms.includes(form)) {
          forms.push(form);
        }
      });

    document
      .querySelectorAll('form[action*="/cart/add"]')
      .forEach((form) => {
        const productIdInput = form.querySelector(
          'input[name="product-id"]'
        );

        if (
          productIdInput &&
          Number(productIdInput.value) === Number(config.productId) &&
          !forms.includes(form)
        ) {
          forms.push(form);
        }
      });

    return forms;
  }

  function findPrimaryNativeForm(config) {
    return (
      findNativeProductForms(config).find((form) =>
        form.querySelector('button[type="submit"][name="add"]')
      ) || null
    );
  }

  function ensureHiddenInput(form, name) {
    let input = form.querySelector(`input[name="${name}"]`);

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }

    return input;
  }

  function syncNativeForm(form, offer) {
    const quantityInput = ensureHiddenInput(form, 'quantity');
    const sellingPlanInput = ensureHiddenInput(form, 'selling_plan');

    quantityInput.value = String(offer.quantity);
    sellingPlanInput.value = offer.planId ? String(offer.planId) : '';

    quantityInput.dispatchEvent(
      new Event('input', { bubbles: true })
    );

    quantityInput.dispatchEvent(
      new Event('change', { bubbles: true })
    );

    sellingPlanInput.dispatchEvent(
      new Event('input', { bubbles: true })
    );

    sellingPlanInput.dispatchEvent(
      new Event('change', { bubbles: true })
    );

    const component = form.closest('product-form-component');

    if (component) {
      component.dataset.quantityDefault = String(offer.quantity);
    }
  }

  function syncAllNativeForms(config, offer) {
    findNativeProductForms(config).forEach((form) => {
      syncNativeForm(form, offer);
    });
  }

  function updateMainAddToCartButtons(config, offer) {
    const label = `Add to cart | ${formatMoney(offer.total, config)}`;

    findNativeProductForms(config).forEach((form) => {
      const button = form.querySelector(
        'button[type="submit"][name="add"]'
      );

      if (!button) return;

      button.dataset.addToCartText = label;
      button.setAttribute('aria-label', label);

      const text = button.querySelector(
        '.add-to-cart-text__content'
      );

      if (text) {
        text.textContent = label;
      }
    });
  }

  function findStickyBars(config) {
    return Array.from(
      document.querySelectorAll(
        `sticky-add-to-cart[data-product-id="${config.productId}"]`
      )
    );
  }

  function updateStickyPriceDisplay(sticky, config, offer) {
    const price = formatMoney(offer.total, config);
    const comparePrice =
      offer.compareTotal > offer.total
        ? formatMoney(offer.compareTotal, config)
        : '';

    const regularPrice = sticky.querySelector(
      '.price__regular .price'
    );

    if (regularPrice) {
      regularPrice.textContent = price;
    }

    const salePrice = sticky.querySelector(
      '.price__sale .price--sale'
    );

    if (salePrice) {
      salePrice.textContent = price;
    }

    const compare = sticky.querySelector(
      '.compare-at-price'
    );

    if (compare) {
      compare.textContent = comparePrice;
    }
  }

  function updateStickyButtons(config, offer) {
    const label = `Add | ${formatMoney(offer.total, config)}`;

    findStickyBars(config).forEach((sticky) => {
      const button = sticky.querySelector(
        '.sticky-add-to-cart__button'
      );

      if (button) {
        button.dataset.addToCartText = label;
        button.setAttribute('aria-label', label);

        const text = button.querySelector(
          '[ref="addToCartText"], .btn__text'
        );

        if (text) {
          text.textContent = label;
        }
      }

      updateStickyPriceDisplay(
        sticky,
        config,
        offer
      );
    });
  }

  function bridgeStickyButton(sticky, config) {
    const button = sticky.querySelector(
      '.sticky-add-to-cart__button'
    );

    if (
      !button ||
      button.dataset.sbOfferBridge === 'true'
    ) {
      return;
    }

    button.dataset.sbOfferBridge = 'true';

    button.addEventListener(
      'click',
      (event) => {
        const form = findPrimaryNativeForm(config);

        const nativeButton = form?.querySelector(
          'button[type="submit"][name="add"]'
        );

        if (!nativeButton) return;

        event.preventDefault();
        event.stopPropagation();

        if (
          typeof event.stopImmediatePropagation ===
          'function'
        ) {
          event.stopImmediatePropagation();
        }

        nativeButton.click();
      },
      true
    );
  }

  function wireStickyButtons(config) {
    findStickyBars(config).forEach((sticky) => {
      bridgeStickyButton(
        sticky,
        config
      );
    });
  }

  function updatePurchaseUI(config, offer) {
    syncAllNativeForms(config, offer);
    updateMainAddToCartButtons(config, offer);
    updateStickyButtons(config, offer);
    wireStickyButtons(config);
  }

  async function getCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) return null;
    return response.json();
  }

  function isAutoGift(line, config) {
    return (
      line.properties?.[config.gift.propertyName] ===
      config.gift.propertyValue
    );
  }

  function dispatchCartRefresh(open = false) {
    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { open }
      })
    );
  }

  async function reconcileGift(config, qualifyingPlanId) {
    if (
      !config.gift?.enabled ||
      !config.gift.variantId ||
      !qualifyingPlanId
    ) {
      return;
    }

    if (window.__solbiomeNativeGiftBusy) {
      return;
    }

    window.__solbiomeNativeGiftBusy = true;
    let changed = false;

    try {
      const cart = await getCart();
      if (!cart) return;

      const qualifies = cart.items.some((line) => {
        const sellingPlanId =
          line.selling_plan_allocation?.selling_plan?.id;

        return (
          Number(line.product_id) === Number(config.productId) &&
          Number(sellingPlanId) === Number(qualifyingPlanId)
        );
      });

      const gifts = cart.items.filter((line) =>
        isAutoGift(line, config)
      );

      if (qualifies && gifts.length === 0) {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: Number(config.gift.variantId),
                quantity: 1,
                properties: {
                  [config.gift.propertyName]:
                    config.gift.propertyValue
                }
              }
            ]
          })
        });

        changed = response.ok;
      }

      if (qualifies && gifts.length > 1) {
        for (const duplicate of gifts.slice(1)) {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              id: duplicate.key,
              quantity: 0
            })
          });

          changed = changed || response.ok;
        }
      }

      if (!qualifies && gifts.length > 0) {
        for (const giftLine of gifts) {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              id: giftLine.key,
              quantity: 0
            })
          });

          changed = changed || response.ok;
        }
      }

      if (changed) {
        dispatchCartRefresh(false);
      }
    } catch (error) {
      console.warn(
        'Solbiome free gift sync failed.',
        error
      );
    } finally {
      window.__solbiomeNativeGiftBusy = false;
    }
  }

  function setError(root, message) {
    const box = root.querySelector(
      '[data-sb-offer-error]'
    );

    if (!box) return;

    box.textContent = message || '';
    box.hidden = !message;
  }

  function setupGiftWatcher(config, qualifyingPlanId) {
    if (
      !config.gift?.enabled ||
      !config.gift.variantId ||
      !qualifyingPlanId
    ) {
      return;
    }

    let timer = null;

    const schedule = () => {
      clearTimeout(timer);

      timer = setTimeout(() => {
        reconcileGift(
          config,
          qualifyingPlanId
        );
      }, 350);
    };

    [
      'product-ajax:added',
      'cart:updated',
      'cart:change',
      'cart:refresh'
    ].forEach((eventName) => {
      document.addEventListener(
        eventName,
        schedule
      );

      document.documentElement.addEventListener(
        eventName,
        schedule
      );
    });

    reconcileGift(
      config,
      qualifyingPlanId
    );
  }

  function init(root) {
    if (root.dataset.sbInitialised === 'true') {
      return;
    }

    const config = parseConfig(root);

    if (!config) return;

    const offers = calculateOffers(config);
    const missing = [];

    if (!offers.monthly_subscription) {
      missing.push(
        'monthly Recharge plan (quantity 1 + frequency "1 month")'
      );
    }

    if (!offers.three_subscription) {
      missing.push(
        '3 month Recharge plan (quantity 3 + frequency "3 month")'
      );
    }

    if (missing.length) {
      setError(
        root,
        `Could not find the ${missing.join(
          ' and '
        )}. Check that the new Recharge plans are active and available on this product.`
      );

      return;
    }

    root.dataset.sbInitialised = 'true';

    const cards = root.querySelector(
      '[data-sb-offer-cards]'
    );

    const oneCard = createCard(
      config,
      offers.one,
      config.labels.oneTitle,
      config.labels.oneSubtitle
    );

    const twoCard = createCard(
      config,
      offers.two,
      config.labels.twoTitle,
      config.labels.twoSubtitle,
      {
        badge: discountLabel(
          offers.two.discount,
          config.labels.twoBadgeSuffix
        )
      }
    );

    const monthlyCard = createCard(
      config,
      offers.monthly_subscription,
      config.labels.monthlyTitle,
      config.labels.monthlySubtitle,
      {
        badge: discountLabel(
          offers.monthly_subscription.discount,
          config.labels.monthlyBadgeSuffix
        )
      }
    );

    const threeCard = createCard(
      config,
      offers.three_subscription,
      config.labels.threeTitle,
      config.labels.threeSubtitle,
      {
        badge: discountLabel(
          offers.three_subscription.discount,
          config.labels.threeBadgeSuffix
        ),
        body: config.labels.threeBody,
        hero: true,
        showGift: true
      }
    );

    cards.append(
      oneCard,
      twoCard,
      monthlyCard,
      threeCard
    );

    let selected =
      offers[config.defaultOffer] ||
      offers.three_subscription;

    function updateSelection() {
      root
        .querySelectorAll(
          '.sb-naternal-offer__card'
        )
        .forEach((card) => {
          const active =
            card.dataset.offer === selected.type;

          card.classList.toggle(
            'is-selected',
            active
          );

          card.setAttribute(
            'aria-pressed',
            active ? 'true' : 'false'
          );
        });

      root.dataset.selectedOffer =
        selected.type;

      updatePurchaseUI(
        config,
        selected
      );
    }

    root
      .querySelectorAll(
        '.sb-naternal-offer__card'
      )
      .forEach((card) => {
        card.addEventListener(
          'click',
          () => {
            const type =
              card.dataset.offer;

            if (!offers[type]) {
              return;
            }

            selected = offers[type];
            updateSelection();
          }
        );
      });

    const observer = new MutationObserver(
      (mutations) => {
        const relevant = mutations.some(
          (mutation) =>
            Array.from(
              mutation.addedNodes
            ).some((node) => {
              if (!(node instanceof Element)) {
                return false;
              }

              return (
                node.matches?.(
                  'product-form-component, form[action*="/cart/add"], sticky-add-to-cart'
                ) ||
                node.querySelector?.(
                  'product-form-component, form[action*="/cart/add"], sticky-add-to-cart'
                )
              );
            })
        );

        if (relevant) {
          window.setTimeout(() => {
            updatePurchaseUI(
              config,
              selected
            );
          }, 50);
        }
      }
    );

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );

    setupGiftWatcher(
      config,
      offers.three_subscription.planId
    );

    updateSelection();
  }

  function initAll(container = document) {
    container
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(init);
  }

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initAll();
    }
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(event.target);
    }
  );
})();
